// src/components/optimiseur/PointsAConfirmer.jsx — LES POINTS À CONFIRMER, EN
// OUI / NON (expert AO, 31.10 : « nouveau fonctionnement, plus simple »).
//
// Pour chaque point, le recruteur répond :
//   - Oui (« le candidat a cette compétence ou l'a déjà pratiquée »), avec,
//     s'il la connaît, la mission (sinon Leman choisit la plus logique — l'ajout
//     est alors « placé par Leman, à vérifier », déplaçable en un clic) et une
//     précision (société, contexte) ;
//   - Non : rien n'est écrit, le point devient un écart à aborder en entretien.
// Rien n'est coché d'office : aucun ajout sans un « Oui » cliqué. Un Oui compte
// 75 dans le score (le serveur le dit : « score X → Y ») ; le CV se régénère
// une fois, quand on a fini.
//
// Toute action de l'analyse arrive ici — « Préciser » compris (31.10 :
// « pourquoi sur ces actions je ne peux pas les implémenter par Leman ? »).
// Celles que le CV montre déjà (`deja_compte`) sont repliées sous les autres :
// leur « Oui » fait écrire les mots de l'exigence sans changer le score ; elles
// ne partent pas dans les questions au candidat.
//
//   GET  /besoins/:bid/cv-ajouts/:cid             → { missions, ajouts }
//   POST /besoins/:bid/cv-ajouts/:cid/reponses    { reponses:[{ critere, oui, mission_cle?, contexte? }] }
//   POST /besoins/:bid/cv-ajouts/:cid/:aid/deplacer { mission_cle }
//   PUT  /besoins/:bid/cv-ajouts/:cid             { ajouts:[{ critere, retirer:true }] }
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appel } from './api';
import DemanderCandidat from './DemanderCandidat';
import ValidationCandidat from './ValidationCandidat';

const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const Spin = () => <i className="bi bi-arrow-repeat tq-spin" aria-hidden="true" />;
const VIDE = { oui: undefined, mission_cle: '', contexte: '' };
const MAX_CONTEXTE = 300;

export default function PointsAConfirmer({ besoinId, candidateId, prenom, points, ecarts = [], niveaux = {}, aCv, peutGenerer = true, regenEnCours, onEcrit, onRegenerer, onReponses, showNotification }) {
  const [reponses, setReponses] = useState({});           // critère → { oui, mission_cle, contexte }
  const [etat, setEtat] = useState({ etat: 'loading', missions: [], ajouts: [] });
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);             // id de l'ajout qu'on déplace / retire
  const [routeReponses, setRouteReponses] = useState(true);
  const [ecartsLocaux, setEcartsLocaux] = useState([]);
  const [aRegenerer, setARegenerer] = useState(false);
  const [demander, setDemander] = useState(false);
  const [versionSuivi, setVersionSuivi] = useState(0);
  const [voirComptes, setVoirComptes] = useState(false);
  const lignesRef = useRef({});

  const charger = useCallback(async () => {
    try {
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-ajouts/${candidateId}`);
      if (absente) { setEtat({ etat: 'route', missions: [], ajouts: [] }); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `Les ajouts n’ont pas pu être lus (${r.status}).`);
      setEtat({ etat: 'ready', missions: Array.isArray(j.missions) ? j.missions : [], ajouts: Array.isArray(j.ajouts) ? j.ajouts : [] });
    } catch (e) { setEtat(x => ({ ...x, etat: 'error', erreur: e.message })); }
  }, [besoinId, candidateId]);
  useEffect(() => { charger(); }, [charger]);

  // Une réponse du candidat reprise depuis son suivi peut viser un point que
  // l'analyse ne liste plus : il reste répondable.
  const liste = useMemo(() => {
    const base = points || [];
    const connus = new Set(base.map(p => p.libelle));
    return [...base, ...Object.keys(reponses).filter(k => !connus.has(k)).map(k => ({ libelle: k, question: k, gain: null }))];
  }, [points, reponses]);
  const repondus = liste.filter(p => typeof reponses[p.libelle]?.oui === 'boolean');
  // Ce qui fait monter le score d'abord ; le déjà compté (le CV le montre en
  // mission, un « Oui » fait écrire les mots de l'exigence sans changer le
  // score) replié dessous — ouvert dès qu'une de ses réponses attend.
  const principaux = liste.filter(p => !p.deja_compte);
  const dejaComptes = liste.filter(p => p.deja_compte);
  const comptesOuverts = voirComptes || dejaComptes.some(p => typeof reponses[p.libelle]?.oui === 'boolean');
  const aDemander = (points || []).filter(p => !p.deja_compte);
  const poser = (critere, patch) => setReponses(s => ({ ...s, [critere]: { ...VIDE, ...(s[critere] || {}), ...patch } }));

  const ecrits = etat.ajouts.filter(a => a.statut === 'valide');
  const sansOui = etat.ajouts.filter(a => a.statut === 'retire' && a.reponse === 'sans_oui').length;
  const tousEcarts = [...new Set([...(ecarts || []), ...ecartsLocaux])].filter(c => !ecrits.some(a => a.critere === c));

  const apres = (j) => {
    if (j?.a_regenerer) setARegenerer(true);
    charger();
    onEcrit?.();
  };

  const enregistrer = async () => {
    if (!repondus.length || busy) return;
    setBusy(true);
    try {
      const corps = { reponses: repondus.map(p => {
        const x = reponses[p.libelle];
        if (!x.oui) return { critere: p.libelle, oui: false };
        const contexte = String(x.contexte || '').trim();
        return { critere: p.libelle, oui: true, ...(x.mission_cle ? { mission_cle: x.mission_cle } : {}), ...(contexte ? { contexte } : {}) };
      }) };
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-ajouts/${candidateId}/reponses`, { method: 'POST', corps });
      if (absente) { setRouteReponses(false); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `Les réponses ont été refusées (${r.status}).`);
      showNotification?.(j.message || 'Réponses enregistrées.', 'success');
      if (Array.isArray(j.ecarts) && j.ecarts.length) setEcartsLocaux(es => [...new Set([...es, ...j.ecarts])]);
      const faits = new Set(repondus.map(p => p.libelle));
      setReponses(s => Object.fromEntries(Object.entries(s).filter(([k]) => !faits.has(k))));
      apres(j);
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  const deplacer = async (a, missionCle) => {
    if (!missionCle || action) return;
    setAction(a.id);
    try {
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-ajouts/${candidateId}/${a.id}/deplacer`, { method: 'POST', corps: { mission_cle: missionCle } });
      if (absente) throw new Error('Le déplacement arrive côté serveur : rien n’a changé.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Le déplacement a été refusé (${r.status}).`);
      showNotification?.(j.message || 'Ajout déplacé.', 'success');
      apres({ a_regenerer: true });
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setAction(null); }
  };

  const retirer = async (a) => {
    if (action) return;
    setAction(a.id);
    try {
      const { r, j, absente } = await appel(`/besoins/${besoinId}/cv-ajouts/${candidateId}`, { method: 'PUT', corps: { ajouts: [{ critere: a.critere, retirer: true }] } });
      if (absente) throw new Error('Le retrait arrive côté serveur : rien n’a changé.');
      if (!r.ok || j.success === false) throw new Error(j.error || `Le retrait a été refusé (${r.status}).`);
      showNotification?.(j.message || 'Retiré du CV.', 'success');
      apres({ a_regenerer: true });
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setAction(null); }
  };

  const regenerer = async () => { await onRegenerer?.(); setARegenerer(false); };

  const copierQuestions = async () => {
    const lignes = aDemander.map(q => `- ${q.question}`);
    try {
      await navigator.clipboard.writeText(`Bonjour ${prenom || ''},\n\nPour présenter au mieux votre profil, pourriez-vous me préciser :\n${lignes.join('\n')}\n\nMerci !`);
      showNotification?.('Questions copiées : prêtes à coller dans votre message au candidat.', 'success');
    } catch (e) { showNotification?.(e.message, 'error'); }
  };

  // « Reprendre avec sa réponse » : le candidat a répondu — Oui, sa réponse en précision.
  const reprendre = (critere, reponse) => {
    poser(critere, { oui: true, contexte: String(reponse || '').replace(/\s+/g, ' ').trim().slice(0, MAX_CONTEXTE) });
    setTimeout(() => lignesRef.current[critere]?.focus(), 0);
  };

  const missions = etat.missions || [];

  const ligne = (p, i) => {
    const x = reponses[p.libelle] || VIDE;
    const idSel = `opt-mission-${candidateId}-${i}`;
    return (
      <li key={p.libelle} className="opt-oui-non__ligne">
        <div className="opt-oui-non__tete">
          <span className="opt-oui-non__question">{p.question}</span>
          {p.gain != null && Number(p.gain) > 0 && <span className="opt-gain">+{p.gain} pts</span>}
          <div className="opt-oui-non__choix" role="group" aria-label={`Réponse pour « ${p.libelle} »`}>
            <button type="button" ref={el => { lignesRef.current[p.libelle] = el; }} className="opt-oui-non__btn" aria-pressed={x.oui === true}
              disabled={busy} onClick={() => poser(p.libelle, { oui: x.oui === true ? undefined : true })}>
              <BI icon="check-lg" /> Oui
            </button>
            <button type="button" className="opt-oui-non__btn opt-oui-non__btn--non" aria-pressed={x.oui === false}
              disabled={busy} onClick={() => poser(p.libelle, { oui: x.oui === false ? undefined : false })}>
              <BI icon="x-lg" /> Non
            </button>
          </div>
        </div>
        {x.oui === true && (
          <div className="opt-oui-non__details">
            <label className="opt-champ" htmlFor={idSel}>
              <span className="opt-champ__lib">Mission</span>
              <select id={idSel} className="erp-field" value={x.mission_cle} disabled={busy}
                onChange={e => poser(p.libelle, { mission_cle: e.target.value })}>
                <option value="">Leman choisit la mission</option>
                {missions.map(m => <option key={m.cle} value={m.cle}>{m.libelle}</option>)}
              </select>
            </label>
            <label className="opt-champ">
              <span className="opt-champ__lib">Précision (société, contexte) — facultatif</span>
              <input type="text" className="erp-field" value={x.contexte} maxLength={MAX_CONTEXTE} disabled={busy}
                placeholder="Par ex. chez OCSIN, sur le portefeuille de contrats de services"
                onChange={e => poser(p.libelle, { contexte: e.target.value })} />
            </label>
          </div>
        )}
        {x.oui === false && <p className="opt-note">Rien ne sera écrit : à aborder en entretien.</p>}
      </li>
    );
  };


  return (
    <section className="opt-section opt-points">
      <div className="opt-section__tete">
        <h3 className="opt-section__titre">Points à confirmer</h3>
        <span className="opt-section__meta">{principaux.length}</span>
        <span className="tuto-bulle__espace" />
        {aDemander.length > 0 && <button type="button" className="tuto-bulle__lien" onClick={copierQuestions}><BI icon="clipboard" /> Copier les questions</button>}
      </div>

      {liste.length > 0 ? (
        <>
          <p className="opt-note">
            <strong>Oui</strong> : le candidat a cette compétence ou l’a déjà pratiquée — elle s’écrit dans le CV et compte 75.
            {' '}<strong>Non</strong> : rien n’est écrit, l’écart s’aborde en entretien.
          </p>
          {principaux.length > 0 && (
            <ul className="opt-oui-non">
              {principaux.map((p, i) => ligne(p, i))}
            </ul>
          )}
          {dejaComptes.length > 0 && (
            <div className="opt-oui-non__comptes">
              <button type="button" className="tuto-bulle__lien" aria-expanded={comptesOuverts} onClick={() => setVoirComptes(v => !v)}>
                <BI icon={comptesOuverts ? 'chevron-up' : 'chevron-down'} /> Déjà comptés : les écrire avec les mots de l’exigence ({dejaComptes.length})
              </button>
              {comptesOuverts && (
                <>
                  <p className="opt-note">Le CV les montre déjà dans une mission. <strong>Oui</strong> : Leman les écrit avec les mots de l’exigence — le CV dit ce que le client cherche, le score ne bouge pas.</p>
                  <ul className="opt-oui-non">
                    {dejaComptes.map((p, i) => ligne(p, principaux.length + i))}
                  </ul>
                </>
              )}
            </div>
          )}
          {!routeReponses
            ? <p className="opt-muet">L’enregistrement des réponses arrive côté serveur : rien n’a été écrit.</p>
            : (
              <div className="opt-oui-non__pied">
                <span className="opt-note">{repondus.length ? `${repondus.length} réponse${repondus.length > 1 ? 's' : ''} à enregistrer` : 'Répondez Oui ou Non : rien n’est écrit sans votre réponse.'}</span>
                <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={enregistrer} disabled={!repondus.length || busy}>
                  {busy ? <Spin /> : <BI icon="check2" />} {busy ? 'Enregistrement…' : 'Enregistrer les réponses'}
                </button>
              </div>
            )}
        </>
      ) : <p className="opt-muet">Aucun point à confirmer sur ce profil. Vous pouvez quand même lui faire relire son CV.</p>}

      {etat.etat === 'error' && <p className="opt-erreur">{etat.erreur}</p>}
      {etat.etat === 'loading' && <p className="opt-muet"><Spin /> Lecture des ajouts…</p>}

      {ecrits.length > 0 && (
        <div className="opt-ecrits">
          <h4 className="opt-sous-titre">Écrit dans le CV</h4>
          {ecrits.map(a => (
            <div key={a.id} className="opt-reponse">
              <div className="opt-reponse__corps">
                <div className="opt-reco__puces">
                  <span className="opt-puce opt-puce--ok"><BI icon="check2" /> compte {Math.max(75, Number(niveaux?.[a.critere]) || 0)}</span>
                  {a.place_par_leman && <span className="opt-puce opt-puce--confirmer"><BI icon="geo-alt" /> Placé par Leman · à vérifier</span>}
                </div>
                <div className="opt-reponse__critere">{a.critere}{a.mission_libelle ? ` — ${a.mission_libelle}` : ''}</div>
                {a.texte && <div className="opt-reponse__texte">{a.texte}</div>}
                <div className="opt-oui-non__actions">
                  {missions.length > 1 && (
                    <label className="opt-champ opt-champ--ligne">
                      <span className="opt-champ__lib">Déplacer vers</span>
                      <select className="erp-field" value="" disabled={!!action} aria-label={`Déplacer « ${a.critere} » vers une autre mission`}
                        onChange={e => deplacer(a, e.target.value)}>
                        <option value="">Choisir une mission…</option>
                        {missions.filter(m => m.cle !== a.mission_cle).map(m => <option key={m.cle} value={m.cle}>{m.libelle}</option>)}
                      </select>
                    </label>
                  )}
                  <button type="button" className="tuto-bulle__lien" onClick={() => retirer(a)} disabled={!!action}>
                    {action === a.id ? <Spin /> : <BI icon="x-lg" />} Retirer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {sansOui > 0 && <p className="opt-note">{sansOui} ajout{sansOui > 1 ? 's' : ''} écrit{sansOui > 1 ? 's' : ''} sans « Oui » {sansOui > 1 ? 'ont été retirés' : 'a été retiré'} du CV : {sansOui > 1 ? 'ils ne comptent' : 'il ne compte'} plus. Répondez au point pour le rétablir.</p>}

      {tousEcarts.length > 0 && (
        <div className="opt-ecrits">
          <h4 className="opt-sous-titre">Écarts à aborder en entretien</h4>
          <ul className="opt-points__liste">
            {tousEcarts.map(c => <li key={c} className="opt-question"><BI icon="chat-left-dots" /> {c}</li>)}
          </ul>
        </div>
      )}

      {aRegenerer && (
        <div className="opt-regen" role="status">
          <BI icon="file-earmark-word" />
          <span>{peutGenerer
            ? 'Le CV a changé : régénérez-le une fois que vous avez fini, pour qu’il porte ces réponses.'
            : 'Le CV a changé : régénérez-le depuis sa ligne du dossier pour qu’il porte ces réponses.'}</span>
          {peutGenerer && (
            <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={regenerer} disabled={regenEnCours}>
              {regenEnCours ? <Spin /> : <BI icon="arrow-repeat" />} {regenEnCours ? 'Génération…' : 'Régénérer le CV'}
            </button>
          )}
        </div>
      )}

      <div className="opt-oui-non__candidat">
        <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" aria-expanded={demander} onClick={() => setDemander(v => !v)}>
          <BI icon="send" /> Demander au candidat
        </button>
        <span className="opt-note">Il répond par un lien sans compte, Leman écrit ses réponses dans le CV.</span>
      </div>
      {demander && (
        <DemanderCandidat besoinId={besoinId} candidateId={candidateId} points={aDemander} aCv={aCv} showNotification={showNotification}
          onLienCree={() => setVersionSuivi(v => v + 1)} />
      )}

      <ValidationCandidat key={`${besoinId}:${candidateId}`} besoinId={besoinId} candidateId={candidateId} version={versionSuivi}
        showNotification={showNotification} onRegenerer={onRegenerer} onReponses={onReponses}
        onLemanFini={() => { setARegenerer(true); charger(); onEcrit?.(); }}
        onReprendre={reprendre} />
    </section>
  );
}
